import Lozenge from '@atlaskit/lozenge';
import { colors, elevation } from '@atlaskit/theme';
import Tooltip from '@atlaskit/tooltip';
import { inject } from 'mobx-react';
import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { redirect } from '../../../facade/window';
import { AnalyticsConsumerProps, withAnalytics } from '../../../hoc/withAnalytics';
import { PRODUCT_KEYS_TO_IDENTITY_APPLICATION, ProductKeys } from '../../../model/productType';
import Store from '../../../store';
import FeatureFlagsStore from '../../../store/featureFlagsStore';
import { Product } from '../../../store/productStore';
import UserStore from '../../../store/userStore';
import { getSessionSynchronisationUrl } from '../../../util/auth';
import { isNewTabClick } from '../../../util/event';
import { ellipsis } from '../../styled/text';
import { cardContainerStyle } from '../style';

import ProductIcon, { IconContainer } from './ProductIcon';

export type ProductCardProps = Omit<Product, 'count'> & {
  featureFlagsStore: FeatureFlagsStore;
  userStore: UserStore;
} & Partial<Product> & {
    className?: string;
    fullScreen?: boolean;
    hideSiteName?: boolean;
    isLozengeVisible?: boolean;
    joinable?: boolean;
    lozengeContent?: ReactNode;
    requestable?: boolean;
    tabIndex?: number;
    tooltipContent?: ReactNode;
  };

const LinkWrapper = styled.a`
  button {
    ${cardContainerStyle};
    background-color: transparent;
    border: 0;
    &:focus,
    &:hover {
      text-decoration: none;
      ${IconContainer} {
        background-color: ${colors.B300};
        ${elevation.e400};
      }
    }
  }
  &:hover {
    text-decoration: none;
  }
`;

const ProductName = styled.div`
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  min-height: 16px;
  margin-bottom: 4px;
  color: ${colors.N800};
`;

const SiteName = styled.div`
  color: ${colors.N100};
  font-size: 12px;
  line-height: 16px;
  min-height: 16px;
  ${ellipsis};
`;

const LozengeWrapper = styled.div`
  margin-top: 8px;
`;
class ProductCard extends React.Component<ProductCardProps & AnalyticsConsumerProps> {
  render() {
    const { className, tooltipContent = this.props.siteName, url, tabIndex } = this.props;
    const focusHidden = tabIndex === -1;
    return (
      <LinkWrapper className={className} href={url} onClick={this.onClick} tabIndex={-1} aria-hidden="true">
        {focusHidden ? (
          <button type="button" tabIndex={tabIndex}>
            {this.renderContent()}
          </button>
        ) : (
          <Tooltip tag="button" content={tooltipContent} hideTooltipOnClick>
            {this.renderContent()}
          </Tooltip>
        )}
      </LinkWrapper>
    );
  }

  renderContent = () => {
    const {
      icon,
      isLozengeVisible = false,
      lozengeContent = '',
      productName,
      productKey,
      cloudId,
      siteName,
      hideSiteName,
    } = this.props;
    return (
      <div data-testid={`start-product__${productKey}_${cloudId}`}>
        <ProductIcon icon={icon} />
        <ProductName>{productName}</ProductName>
        {!hideSiteName && <SiteName>{siteName}</SiteName>}
        {isLozengeVisible && lozengeContent && (
          <LozengeWrapper>
            <Lozenge appearance="inprogress">{lozengeContent}</Lozenge>
          </LozengeWrapper>
        )}
      </div>
    );
  };

  private onClick = async (event: React.MouseEvent<HTMLElement>) => {
    const { cloudId, fullScreen, joinable = false, productKey, siteName, requestable = false, url } = this.props;
    const isOpeningInNewTab = isNewTabClick(event);
    if (!isOpeningInNewTab) {
      event.preventDefault();
    }

    const cardsContainer = event.currentTarget.closest('[data-testid="product-container"]');
    const cardElements = cardsContainer && cardsContainer.querySelectorAll('a');

    function getCardIndex(currentCard: HTMLElement, cards: NodeListOf<HTMLAnchorElement> | null) {
      if (!cards || !cards.length) {
        return null;
      }
      const cardsArr = Array.from(cards);
      for (const card of cardsArr) {
        if (card === currentCard || card.contains(currentCard)) {
          return cardsArr.indexOf(card);
        }
      }
    }

    await this.props.analytics.ui('productCard', 'clicked', {
      cloudId,
      fullScreen,
      joinable,
      product: productKey,
      requestable,
      siteName,
      url,
      index: getCardIndex(event.currentTarget, cardElements),
    });

    if (!isOpeningInNewTab) {
      redirect(
        getSessionSynchronisationUrl(
          url,
          this.props.userStore.email,
          PRODUCT_KEYS_TO_IDENTITY_APPLICATION[productKey as ProductKeys],
        ),
      );
    }
  };
}

export default inject(({ featureFlagsStore, userStore }: Store) => ({
  featureFlagsStore,
  userStore,
}))(withAnalytics(ProductCard));
